<template>
    <div id="reservations" class="container py-5">
        <breadcrumb />

        <div class="mb-4 d-flex align-items-center justify-content-between g-8 flex-wrap">
            <h1>
                <i class="icons-suitcase font-28 align-middle"></i>
                {{ __('reservations.reservation_trips') }}
            </h1>
            <a :href="`${config.siteUrl}/${culture}/help-center/search-itinerary`" target="_blank" class="link link--md" v-if="Object.keys(getReservations).length > 0">{{ __('reservations.cant_find_reservation') }}</a>
        </div>

        <!--- EMPTY STATE ---->
        <div class="text-center" v-if="getCategoryWithData === null && Object.keys(getReservations).length === 0">
            <img :src="`/assets/img/login/${config.siteName}/traveler-airport.${config.Code === 'TB' ? 'svg' : 'png'}`" 
                alt="travel" 
                width="280"
                style="width: 100%; max-width: 520px;">
            <p class="my-24 font-18 font-600">
                {{ user?.name || "Hey" }}, {{ __('reservations.no_upcoming_trips', ) }}<br> {{__('reservations.where_to_go') }}
            </p>
            <a class="mb-24 btnPrimary" :href="`${config.siteUrl}`">{{ __('reservations.search_trip_options') }}</a><br>
            <a class="link" :href="`${config.siteUrl}/${culture}/help-center/search-itinerary`">
                {{ __('reservations.cant_find_reservation') }}
            </a>
        </div>

        <!-- TABS -->
        <menu class="ReservationsTabs" v-if="!getLoading && Object.keys(getReservations).length > 0">
            <li>
                <button @click="setCategory('pending')" :class="{ active: getCategory === 'pending' }" v-if="getQuotesData > 0">
                    {{ __('reservations.quote') }}
                </button>
            </li>
            <li>
                <button @click="setCategory('upcoming')" :class="{ active: getCategory === 'upcoming' }" :disabled="getUpcomingData === 0">
                    {{ __('reservations.upcoming') }}
                </button>
            </li>
            <li>
                <button @click="setCategory('pending-payment')" :class="{ active: getCategory === 'pending-payment' }" :disabled="getPendingData === 0">
                    {{ __('reservations.progressTitle') }}
                </button>
            </li>
            <li>
                <button @click="setCategory('completed')" :class="{ active: getCategory === 'completed' }" :disabled="getCompletedData === 0">
                    {{ __('reservations.past') }}
                </button>
            </li>
            <li>
                <button @click="setCategory('cancelled')" :class="{ active: getCategory === 'cancelled' }" :disabled="getCancelledData === 0">
                    {{ __('reservations.cancelled') }}
                </button>
            </li>
        </menu>

        <!-- LOADER -->
        <loading v-if="getLoading"></loading>

        <!-- CARDS -->
        <div class="list-unstyled d-flex flex-column g-28" v-if="Object.keys(getReservations).length > 0">
            <section class="ReservationsCard" v-for="(item, index) in getReservations.data">
                <div class="ReservationsCard__cover"  :class="{'justify-content-end' : item.bookingStatus === 'COMPLETED' || item.bookingStatus === 'QUOTE'}">
                    <img :src="item.imageUrl" alt="cover" width="610">

                    <span v-if="item.bookingStatus !== 'COMPLETED' && item.bookingStatus !== 'QUOTE'"
                          class="cover__status"
                          :class="{
                            'cover__status--success': item.bookingStatus === 'OK',
                            'cover__status--warning': item.bookingStatus === 'PROGRESS',
                            'cover__status--error': item.bookingStatus === 'CANCELLED',
                        }">
                        {{ __(`reservations.${item.bookingStatus.toLowerCase()}`) }}
                    </span>
                    <div>
                        <ul class="list-unstyled d-flex align-items-center g-12">
                            <li v-for="n in item.tags" class="cover__servicehired"><i :class="n"></i></li>
                        </ul>

                        <h2 class="mb-0 font-16 font-md-20"> 
                            {{ item.productType === 'hotel' || item.productType === 'package' ? item.name : item.destination }}
                        </h2>
                        <p class="mb-0 font-14 font-md-16"> {{ item.checkoutStartDate | date('D MMM') }} - {{ item.checkoutEndDate | date('D MMM') }} • {{item.destination}}</p>
                    </div>
                </div>
                <div class="ReservationsCard__footer" >
                    <div>
                        <p class="mb-12 text-right">
                            <span class="d-block font-12 font-subtle">{{ __('reservations.locator', [config.appName]) }}</span>
                            {{ item.bookingReference }}
                        </p>
                        <p class="mb-12 text-right font-14"
                            v-if="item.bookingStatus === 'PROGRESS' && !item.extraInfo.hotelCollect">
                            {{ __('reservations.total') }}
                            <span >
                                <currency-display :amount="item.totalAmount" :showCurrencyCode="true" :currencyCode="item.currency" :applyDecimals="false" :reduceIsoFont="true" class="d-block font-16 font-600"></currency-display>
                            </span>
                        </p>
                    </div>
                    <div class="d-flex align-items-center justify-content-end g-32 flex-md-column-reverse align-items-md-end g-md-16">
                        <a :href="`https://www.pricetravel.com/${culture}/help-center/itinerary?MasterLocatorId=${item.bookingReference}&Email=${item.email}`" 
                            target="_blank" 
                            rel="noreferrer noopenner"
                            class="btnTertiary btnTertiary--xs"
                            :class="{ 'flex-grow-1': item.bookingStatus === 'PROGRESS' &&  !item.extraInfo.hotelCollect }">
                            {{ __('reservations.see_itinerary') }}
                        </a>
                        <button type="button" 
                            class="btnPrimary btnPrimary--xs flex-grow-1" 
                            v-if="(item.bookingStatus === 'PROGRESS' &&  !item.extraInfo.hotelCollect) || item.bookingStatus === 'QUOTE'" 
                            @click.stop.prevent="payOnlineReservation(item)">
                            {{ __('reservations.pay_reservation') }}
                        </button>
                    </div>
                </div>
            </section>
        </div>

        <div class="mt-4 d-center">
            <paginator v-if="Object.keys(getReservations).length > 0 && getTotalReservations > 10" />
        </div>
    </div>
</template>

<script>
    import { storeToRefs } from 'pinia';
    import { useUserStore } from '../stores/user';
    import GlobalUtil from '../../utils/pricetravel/shared/globalUtil';
    const fn = window.__pt.fn;
    const site = window.__pt.settings.site;
    const _paramQuery = fn.search();
    const userLocation = window.__pt.userLocation;
    const _globalUtil = new GlobalUtil(_paramQuery, site);

    export default {

        data() {
            return {
                config: window.__pt.settings.site,
                culture: __pt.cultureData.cultureCode,
                categoryWithData: null,
            }
        },
        setup() {
            const useUser = useUserStore();
            const { setReservation, getReservationService, setLoading, setCurrentPage, setCategoryName, setCache, getAllReservations } = useUser;
            const { getReservations, getCurrentPage, getLoading, getCategory, getTotalReservations, getUpcomingData, getPendingData, getCompletedData, getCancelledData, getCategoryWithData, getQuotesData } = storeToRefs(useUser);
            
            return { setReservation, getReservations, getCurrentPage, getReservationService, setLoading, getLoading, setCurrentPage, setCategoryName, getCategory, setCache, getTotalReservations, getAllReservations, getUpcomingData, getPendingData, getCompletedData, getCancelledData, getCategoryWithData, getQuotesData }
        },
        async beforeMount() {
            await this.getAllReservations();
        },
        props: {
            user: null,
        },
        methods: {
            setCategory(category) {
                this.setCategoryName(category);
                this.setCurrentPage(1);
            },
            async payOnlineReservation(reservation) {
                this.setLoading(true);
                try {
                    const headers = {
                        headers: {
                            "X-CSRF-TOKEN": _globalUtil.getCSFRToken()
                        }
                    };
                    const params = {
                        id: reservation.bookingReference,
                        email: reservation.email
                    };
                    const response = await axios.post(site.siteUrl + "/hotel/api/payment-online", params, headers);

                    if (
                        response &&
                        response.status === 200 &&
                        response.data.status === "ok" &&
                        response.data.urlRedirect !== ""
                    ) {
                        window.location.href = response.data.urlRedirect;
                        return;
                    }
                } catch (error) {
                    this.onError(error, 'error');
                }

                this.setLoading(false);
            },
            async syncReservation(reservation) {
                this.setLoading(true);
                let headers = {
                    headers: {
                        "X-CSRF-TOKEN": _globalUtil.getCSFRToken()
                    }
                }
                let params = {
                    "id": reservation.id,
                    "email": reservation.email
                }
                let response = await axios.post(site.siteUrl + "/hotel/api/user/sync-booking", params, headers).catch((data) => this.onError(data, 'error'));
                if (response && response.status) {
                    this.setLoading(false);
                    window.location.reload();
                }
            }
        }
    }
</script>